import {
    createColumns,
    createInternalLinkFormatter,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { DayOffResponse } from '../../types/response'

/**
 * Get columns for DayOff List
 * Sortable fields: day_off_id, day_off_type, day_off_status, employee_name, department_name
 */
export const getDayOffListColumns = (
    t: TFunction
): DataTableColumn<DayOffResponse>[] =>
    createColumns<DayOffResponse>([
        {
            key: 'day_off_id',
            title: 'ID',
            width: 80,
            sortable: true,
            pinned: 'left',
            formatter: createInternalLinkFormatter({
                pathKey: 'dayOffDetail',
                pathParams: (value) => ({ id: value }),
            }),
        },
        {
            key: 'employee_name',
            title: t('employee_name'),
            width: 180,
            sortable: true,
        },
        {
            key: 'department_name',
            title: t('department_name'),
            width: 180,
            sortable: true,
        },
        {
            key: 'position_name',
            title: t('position'),
            width: 150,
        },
        {
            key: 'number_of_day_off',
            title: t('number_of_day_off'),
            width: 100,
            cellClassName: 'text-center',
            headerClassName: 'text-center',
        },
        {
            key: 'day_off_type',
            title: t('day_off_type'),
            width: 130,
            sortable: true,
            render: (value) => t(value),
        },
        {
            key: 'day_off_status',
            title: t('day_off_status'),
            width: 140,
            sortable: true,
            render: (value) => {
                const colorClass =
                    value === 'pending' || value === 'leader-approved'
                        ? 'text-orange-500'
                        : value === 'approved'
                          ? 'text-green-600'
                          : 'text-red-600'
                return <span className={colorClass}>{t(value)}</span>
            },
        },
        {
            key: 'day_off_range_time',
            title: t('day_off_range_time'),
            width: 220,
            render: (_, row) => {
                const rangeTime = row?.day_off_range_time
                if (!rangeTime?.length) return null

                const firstItem = rangeTime[0]
                const optionLabel =
                    firstItem.day_off_option === 0
                        ? t('morning')
                        : firstItem.day_off_option === 1
                          ? t('afternoon')
                          : t('all_day')

                return `${dayjs(firstItem.start_day_off).format('YYYY/MM/DD')} - ${optionLabel}`
            },
        },
        {
            key: 'day_off_reason',
            title: t('day_off_reason'),
            width: 200,
        },
        {
            key: 'day_off_leader_confirmed',
            title: t('leader'),
            width: 120,
            render: (value, row) => {
                const status = row?.day_off_status
                if (status === 'cancelled')
                    return <span className="text-gray-400">---</span>
                if (value)
                    return (
                        <span className="text-green-600">{t('approved')}</span>
                    )
                if (status === 'approved' || status === 'rejected')
                    return <span className="text-gray-400">---</span>
                return <span className="text-orange-500">{t('pending')}</span>
            },
        },
        {
            key: 'day_off_bod_confirmed',
            title: t('bom/bod'),
            width: 120,
            render: (value, row) => {
                if (row?.day_off_status === 'cancelled')
                    return <span className="text-gray-400">---</span>
                const colorClass = value ? 'text-green-600' : 'text-orange-500'
                return (
                    <span className={colorClass}>
                        {t(value ? 'approved' : 'pending')}
                    </span>
                )
            },
        },
    ])
